import React from 'react'

interface Props {
	poor: number | undefined /** A score from 0.0 to 1.0 */
	good: number | undefined /** A score from 0.0 to 1.0 */
	great: number | undefined /** A score from 0.0 to 1.0 */
	minLabel?: string
	midLabel?: string
	maxLabel?: string
	minimal?: boolean
	hideLabels?: boolean
	comparison?: boolean
}

/**
 * Render a set of three ring charts showing values for poor, good and great
 */
const RingChartSet: React.FC<Props> = function(props) {
	const ratios = roundTo100([props.poor || 0, props.good || 0, props.great || 0])
	return (
		<>
			{
				props.minLabel && props.poor !== undefined &&
				<div className={props.comparison ? 'ring-chart -comparison' : 'ring-chart'}>
					<div className="ringdata -poor">
						<div className="rings">
							<svg className="svg" viewBox="0 0 36 36">
								<g className="ring" style={{ transform: 'scale(1) rotate(-180deg)' }}>
									<circle className="background"
										cx="50%" cy="50%"
										strokeWidth={props.minimal ? '5' : '4'}
										r={props.minimal ? '14.960564651' : '15.915'} /> 
									<circle className="value"
										cx="50%" cy="50%"
										strokeWidth={props.minimal ? '5' : '4'}
										r={props.minimal ? '14.960564651' : '15.915'}
										strokeDasharray={ratios[0] + ', 100'} />
								</g>
							</svg>
						</div>
						<p className="data">
							<span className="result"><span className="value">{ratios[0]}<span className="unit">%</span></span></span>
							{!props.hideLabels && (
								<small className="type">{props.minLabel}</small>
							)}
						</p>
					</div>
				</div>
			}
			{
				props.midLabel && props.good !== undefined &&
				<div className={props.comparison ? 'ring-chart -comparison' : 'ring-chart'}>
					<div className="ringdata -good">
						<div className="rings">
							<svg className="svg" viewBox="0 0 36 36">
								<g className="ring" style={{ transform: 'scale(1) rotate(-180deg)' }}>
									<circle className="background"
										cx="50%" cy="50%"
										strokeWidth={props.minimal ? '5' : '4'}
										r={props.minimal ? '14.960564651' : '15.915'} /> 
									<circle className="value"
										cx="50%" cy="50%"
										strokeWidth={props.minimal ? '5' : '4'}
										r={props.minimal ? '14.960564651' : '15.915'}
										strokeDasharray={ratios[1] + ', 100'} />
								</g>
							</svg>
						</div>
						<p className="data">
							<span className="result"><span className="value">{ratios[1]}<span className="unit">%</span></span></span>
							{!props.hideLabels && (
								<small className="type">{props.midLabel}</small>
							)}
						</p>
					</div>
				</div>
			}
			{
				props.maxLabel && props.great !== undefined &&
				<div className={props.comparison ? 'ring-chart -comparison' : 'ring-chart'}>
					<div className="ringdata -great">
						<div className="rings">
							<svg className="svg" viewBox="0 0 36 36">
								<g className="ring" style={{ transform: 'scale(1) rotate(-180deg)' }}>
									<circle className="background"
										cx="50%" cy="50%"
										strokeWidth={props.minimal ? '5' : '4'}
										r={props.minimal ? '14.960564651' : '15.915'} /> 
									<circle className="value"
										cx="50%" cy="50%"
										strokeWidth={props.minimal ? '5' : '4'}
										r={props.minimal ? '14.960564651' : '15.915'}
										strokeDasharray={ratios[2] + ', 100'} />
								</g>
							</svg>
						</div>
						<p className="data">
							<span className="result"><span className="value">{ratios[2]}<span className="unit">%</span></span></span>
							{!props.hideLabels && (
								<small className="type">{props.maxLabel}</small>
							)}
						</p>
					</div>
				</div>
			}
		</>
	)
}

/**
 * Take an array of numbers, scale them so they are out of 100 and sum to 100.
 * This avoids rounding issues resulting in numbers that don't sum to 100 which
 * looks weird to users.
 */
function roundTo100(values: number[]) {
	const total = values.reduce((a, b) => a + b, 0)
	const targetTotal = Math.round(Math.min(100, total * 100)) /* They may not actually be meant to sum to 100, so we calculate the target */
	const scaled = values.map(v => total === 0 ? 0 : (v / total) * targetTotal)
	const floored = scaled.map(Math.floor)
	const remainder = targetTotal - floored.reduce((a, b) => a + b, 0)

	// Distribute the remaining percentage points to the largest fractional parts
	const fractions = scaled.map((v, i) => ({ i, frac: v - floored[i] }))
	fractions.sort((a, b) => b.frac - a.frac)
	let k = 0
	for (let j = 0; j < remainder; j++) {
		floored[fractions[(k++) % fractions.length].i]++
	}
	return floored
}

export default RingChartSet
